import { BtcScheduleContent } from '@optools/services/operator-gateway/btc/types';
import {
  MediaFeedType,
  MediaType,
} from '@optools/services/operator-gateway/types/generated/graphql';

/**
 * Filters and processes media content for provisioning.
 *
 * This function performs the following steps:
 * 1. Filters the input content to separate video and audio media types.
 * 2. Sorts the audio tracks by language, prioritizing English first and Spanish second.
 * 3. Reduces any duplicate HOME, AWAY, or NATIONAL feeds.
 * 4. Ensures that if there is a NATIONAL video feed, only the NATIONAL feeds are returned.
 *
 * @param content - An array of `BtcScheduleContent` objects to be processed.
 * @returns An array of `BtcScheduleContent` objects that are filtered and processed for provisioning.
 */
export function getMediaForProvisioning(content: BtcScheduleContent[]): BtcScheduleContent[] {
  // filter Video content into a separate list.
  const videoContent = content.filter(({ mediaState }) => mediaState.mediaType === MediaType.VIDEO);

  // sort the audio tracks by language where english is first and spanish is second.
  const audioContent = content
    .filter(({ mediaState }) => mediaState.mediaType === MediaType.AUDIO)
    .sort((a, b) => {
      if (a.language === 'en' && b.language === 'en') {
        return 0;
      } else if (a.language === 'en' && b.language !== 'en') {
        return -1;
      } else {
        return 1;
      }
    });

  // reduce any duplicate HOME, AWAY, or NATIONAL feed.
  const { reducedFeeds } = [...videoContent, ...audioContent].reduce(
    (acc, current) => {
      if (!acc.seen[current.feedType]) {
        acc.seen[current.feedType] = true;
        acc.reducedFeeds.push(current);
      }

      return acc;
    },
    { reducedFeeds: [] as BtcScheduleContent[], seen: {} as Record<MediaFeedType, boolean> },
  );

  // Sort the feeds so that away is first and home is second this will create a consistent user experience.
  const sortedFeeds = reducedFeeds.sort((a, b) => {
    if (a.feedType === MediaFeedType.AWAY && b.feedType === MediaFeedType.HOME) {
      return -1;
    } else if (a.feedType === MediaFeedType.HOME && b.feedType === MediaFeedType.AWAY) {
      return 1;
    } else {
      return 0;
    }
  });

  // National feeds are unique in the sense there is only one video feed per game and due to that
  // we should not show the feeds for home and away in that scenario as they will not be marked as AFS(Available For Streaming).
  return sortedFeeds.some(
    (item) =>
      item.feedType === MediaFeedType.NATIONAL && item.mediaState.mediaType === MediaType.VIDEO,
  )
    ? sortedFeeds.filter((item) => item.feedType === MediaFeedType.NATIONAL)
    : sortedFeeds.filter((item) => item.feedType !== MediaFeedType.NATIONAL);
}
